import {
    getOptionalTargetLanguage,
    getOptionalViewModeByTextLanguage,
    hasSourceScriptText,
    TARGET_LANGUAGE
} from "./translation-direction";

export const getTextValue = (value) => String(value || "");

export const getInitialTextareaTranslationState = () => ({
    submitText        : "",
    shouldBlockSubmit : false,
    required          : false,
    ready             : true,
    status            : "idle",
    viewMode          : "de",
    dirty             : false,
    canTranslate      : false,
    language          : "",
    targetLang        : TARGET_LANGUAGE
});

export const createTextareaTranslationState = ({
        value,
        initialTranslatedValue,
        canTranslate,
        strictTranslate,
        language,
        languageHint
    }) => {
    const initialDeText = getTextValue(initialTranslatedValue);
    const visibleValue = strictTranslate ? (initialDeText || getTextValue(value)) : getTextValue(value);

    if (!canTranslate || !strictTranslate) {
        return {
            deText      : visibleValue,
            nativeText  : "",
            visibleText : visibleValue,
            viewMode    : getOptionalViewModeByTextLanguage(language, languageHint),
            deDirty     : false,
            nativeDirty : false,
            status      : "idle"
        };
    }

    if (initialDeText) {
        return {
            deText      : initialDeText,
            nativeText  : null,
            visibleText : initialDeText,
            viewMode    : "de",
            deDirty     : false,
            nativeDirty : false,
            status      : "idle"
        };
    }

    return {
        deText      : "",
        nativeText  : visibleValue,
        visibleText : visibleValue,
        viewMode    : "native",
        deDirty     : false,
        nativeDirty : !!visibleValue.trim(),
        status      : visibleValue.trim() ? "needs_translation" : "idle"
    };
};

export const textareaTranslationReducer = (state, action) => {
    switch (action.type) {
        case "reset":
            return createTextareaTranslationState(action.payload);

        case "change":
            if (!action.canTranslate) {
                return {
                    ...state,
                    deText      : action.text,
                    visibleText : action.text,
                    deDirty     : false,
                    nativeDirty : false,
                    status      : "idle"
                };
            }

            if (!action.strictTranslate) {
                return {
                    ...state,
                    deText      : state.viewMode === "de" ? action.text : state.deText,
                    nativeText  : state.viewMode === "native" ? action.text : state.nativeText,
                    visibleText : action.text,
                    deDirty     : false,
                    nativeDirty : false,
                    status      : "idle"
                };
            }

            if (state.viewMode === "native") {
                return {
                    ...state,
                    nativeText  : action.text,
                    visibleText : action.text,
                    deDirty     : false,
                    nativeDirty : !!action.text.trim(),
                    status      : action.text.trim() ? "needs_translation" : "idle"
                };
            }

            const deDirty = hasSourceScriptText(action.text, action.language);

            return {
                ...state,
                deText      : action.text,
                nativeText  : null,
                visibleText : action.text,
                deDirty,
                nativeDirty : false,
                status      : deDirty ? "needs_translation" : "idle"
            };

        case "loading":
            return {
                ...state,
                status: "loading"
            };

        case "show_native":
            return {
                ...state,
                visibleText : state.nativeText || "",
                viewMode    : "native",
                deDirty     : false,
                nativeDirty : false,
                status      : "idle"
            };

        case "show_de":
            return {
                ...state,
                visibleText : state.deText,
                viewMode    : "de",
                deDirty     : false,
                nativeDirty : false,
                status      : "idle"
            };

        case "translated_to_native":
            return {
                ...state,
                deText      : action.deText,
                nativeText  : action.nativeText,
                visibleText : action.nativeText,
                viewMode    : "native",
                deDirty     : false,
                nativeDirty : false,
                status      : "idle"
            };

        case "translated_to_de":
            return {
                ...state,
                deText      : action.deText,
                nativeText  : action.nativeText,
                visibleText : action.deText,
                viewMode    : "de",
                deDirty     : false,
                nativeDirty : false,
                status      : "translated"
            };

        case "translated_optional":
            return {
                ...state,
                deText      : action.viewMode === "de" ? action.text : state.deText,
                nativeText  : action.viewMode === "native" ? action.text : state.nativeText,
                visibleText : action.text,
                viewMode    : action.viewMode,
                deDirty     : false,
                nativeDirty : false,
                status      : "translated"
            };

        case "error":
            return {
                ...state,
                status: state.nativeDirty || state.deDirty ? "needs_translation" : "idle"
            };

        default:
            return state;
    }
};

export const getTextareaTranslationSubmitState = ({
        state,
        canTranslate,
        strictTranslate,
        language
    }) => {
    const dirty = strictTranslate && (
        (state.viewMode === "native" && state.nativeDirty) ||
        (state.viewMode === "de" && state.deDirty)
    );
    const needsGermanView = strictTranslate && state.viewMode === "native";
    const shouldBlockSubmit = strictTranslate && canTranslate &&
        !!state.visibleText.trim() &&
        (state.status === "loading" || dirty || needsGermanView);

    return {
        submitText        : strictTranslate && canTranslate ? state.deText : state.visibleText,
        shouldBlockSubmit,
        required          : shouldBlockSubmit,
        ready             : !shouldBlockSubmit,
        status            : state.status,
        viewMode          : state.viewMode,
        dirty             : dirty || needsGermanView,
        translatedFromText: state.viewMode === "native" ? state.deText : (state.nativeText || ""),
        canTranslate,
        language,
        targetLang        : strictTranslate ? TARGET_LANGUAGE : getOptionalTargetLanguage(language, state.viewMode)
    };
};
